import React, { useMemo, useState } from 'react';
import { useImmutableCallback } from '@agro1desenvolvimento/react-hooks';
import { Printer } from 'lucide-react';
import ModalListHeader from '../../components/ModalList/modal-list-header';
import PendingAproveList from './PendingAproveList';
import usePendingApprovesStore from './store';
import { TypedAplication } from '../../@types';
import PendingAproveFilter from './PendingAproveFilter';
import PendingAproveActions from './PendingAproveActions';

import PendingAproveReportModal from './PendingAproveReportModal';

const PendingAprovePanelContent: React.FC = () => {
  const { notifications, isLoading, textInput, setTextInput, refreshAproves } =
    usePendingApprovesStore();
  const [isPendingReportModalVisible, setIsPendingReportModalVisible] =
    useState(false);
  const notificationsConcatenated = useMemo(() => {
    const sortedAplicacoes = [...notifications.aplicacoes].sort((a, b) =>
      (a.auditoriaAplicacao?.dtIni ?? '').localeCompare(
        b.auditoriaAplicacao?.dtIni ?? '',
      ),
    );

    return [
      ...notifications.recomendacoes.map(
        (item) => ({ ...item, type: 'recomendacoes' }) as TypedAplication,
      ),
      ...notifications.aqilaRecomendacoes.map(
        (item) => ({ ...item, type: 'aqilaRecomendacoes' }) as TypedAplication,
      ),
      ...sortedAplicacoes.map(
        (item) => ({ ...item, type: 'aplicacoes' }) as TypedAplication,
      ),
    ];
  }, [notifications]);

  const filteredNotifications = useMemo(() => {
    const normalizedSearch = textInput.trim().toLowerCase();

    if (!normalizedSearch) {
      return notificationsConcatenated;
    }

    return notificationsConcatenated.filter((item) => {
      const tipoOperacao =
        'tipoOperacao' in item ? item.tipoOperacao?.toLowerCase() ?? '' : '';
      const propriedadeDescricao =
        item.propriedade?.descricao?.toLowerCase() ?? '';
      const glebaDescricao =
        'gleba' in item ? item.gleba?.descricao?.toLowerCase() ?? '' : '';
      const usuarioNome =
        'usuario' in item ? item.usuario?.nome?.toLowerCase() ?? '' : '';

      return [
        tipoOperacao,
        propriedadeDescricao,
        glebaDescricao,
        usuarioNome,
      ].some((value) => value.includes(normalizedSearch));
    });
  }, [notificationsConcatenated, textInput]);

  const handleClearSearch = useImmutableCallback(() => {
    setTextInput('');
  });

  const handleSearchTextChange = useImmutableCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setTextInput(event.target.value);
    },
  );

  const handlePendingReportModalOpen = useImmutableCallback(() => {
    setIsPendingReportModalVisible(true);
  });

  const handlePendingReportModalClose = useImmutableCallback(() => {
    setIsPendingReportModalVisible(false);
  });

  return (
    <>
      <div className="integration-errors-content integration-errors-content-pending-approves">
        <ModalListHeader
          icon={<Printer size={16} />}
          title="Aprovações Pendentes"
          onDownloadClick={handlePendingReportModalOpen}
        />
        <PendingAproveFilter
          textInput={textInput}
          onSearchTextChange={handleSearchTextChange}
          onClearSearch={handleClearSearch}
        />
        <PendingAproveActions
          onRefreshPressed={refreshAproves}
          isLoading={isLoading}
          itens={filteredNotifications}
        />
        <PendingAproveList
          itens={filteredNotifications}
          isLoading={isLoading}
        />
      </div>
      <PendingAproveReportModal
        visible={isPendingReportModalVisible}
        onHide={handlePendingReportModalClose}
      />
    </>
  );
};

export default PendingAprovePanelContent;
